/**
 * Dimensions (P5 Wave 4 §4) — the personality dimensions the Quill engine
 * tracks, each with a decay half-life (τ), an importance weight, a
 * charter-sensitive flag, and an internal-only visibility flag.
 *
 * ── WHAT CHANGED (2026-08-03 L5 audit) ────────────────────────────────────
 *
 * Three of those four columns were rendered READ-ONLY here, with a note saying
 * an editor was out of scope. The backend's PATCH had accepted all three from
 * the day it shipped and nothing had ever called it with them, so three values
 * quill-engine genuinely reads had no way in at all. Grant's decision was to
 * make them editable. Every one of them is a production write to the live
 * engine, so every one goes through a dialog, a required reason, the change
 * log, and the engine's weights-cache invalidation.
 *
 * ── THE FOUR CONTROLS, AND WHY THEY DIFFER ────────────────────────────────
 *
 *   τ + importance      one dialog (DimensionTuningDialog). They are one
 *                       decision about one dimension, and an inline cell input
 *                       would make a production write out of a keystroke.
 *                       Bounds are checked here AND at the route (400
 *                       DIMENSION_BOUNDS decides a disagreement).
 *
 *   charter-sensitive   ASYMMETRIC, and for the same reason the reveal below
 *                       it is. quill-engine tags facts sensitive from this
 *                       column and the Mirror's gate drops the
 *                       sensitive-and-unconfirmed ones, so CLEARING it lets
 *                       unconfirmed claims on that axis reach a bereaved
 *                       family. Adding the gate is one click; removing it needs
 *                       the dimension key typed (sent as
 *                       `confirm_charter_sensitive`; 428
 *                       CHARTER_SENSITIVE_CONFIRM_REQUIRED handled defensively).
 *
 *   internal-only       DECISION 7.4, unchanged. Hiding is one click; revealing
 *                       needs the typed key (`confirm_reveal`, 428
 *                       REVEAL_CONFIRM_REQUIRED). A permanently-internal
 *                       dimension (coping_style) gets NO switch at all — F4.
 *
 * ── coping_style IS NOT REACHABLE FROM ANY OF THIS ────────────────────────
 *
 * The new editors write `decay_half_life_days`, `importance` and
 * `is_charter_sensitive`. None of them is `is_internal_only`, none of them is
 * sent alongside it, and the row keeps its lock chip and its "always internal"
 * note throughout. quill-engine holds the floor in code regardless
 * (dimensionMap INTERNAL_ONLY_DIMENSION_KEYS, noticedService NEVER_SURFACED),
 * and the route answers 409 DIMENSION_PERMANENTLY_INTERNAL to any attempt.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Switch,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import { useQuillHealth } from '../QuillHealthContext';
import DataTable, { Column } from '../../common/DataTable';
import EmptyState from '../../common/EmptyState';
import QuillActionConfirm from '../common/QuillActionConfirm';
import { colors, typography } from '../../../theme/designTokens';
import { QuillApiError } from '../../../services/quillAdminService';
import { dimensionsApi, PersonalityDimension } from './api';
import RevealConfirmDialog from './RevealConfirmDialog';
import DimensionTuningDialog, { DimensionTuningValues } from './DimensionTuningDialog';

type Severity = 'success' | 'error';

const DimensionsPage: React.FC = () => {
  const { status } = useQuillHealth();

  const [dimensions, setDimensions] = useState<PersonalityDimension[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [pendingEnable, setPendingEnable] = useState<PersonalityDimension | null>(null);
  const [pendingReveal, setPendingReveal] = useState<PersonalityDimension | null>(null);
  const [pendingTuning, setPendingTuning] = useState<PersonalityDimension | null>(null);
  const [pendingGate, setPendingGate] = useState<PersonalityDimension | null>(null);
  const [pendingUngate, setPendingUngate] = useState<PersonalityDimension | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: Severity }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    dimensionsApi
      .getDimensions()
      .then((rows) => setDimensions(rows))
      .catch((e) => setLoadError(e instanceof Error ? e.message : 'Failed to load dimensions.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const applyUpdatedRow = (key: string, returned: PersonalityDimension | undefined, fallback: Partial<PersonalityDimension>) => {
    setDimensions((prev) =>
      prev
        ? prev.map((d) => (d.dimension_key === key ? (returned ?? { ...d, ...fallback }) : d))
        : prev
    );
  };

  const notify = (message: string, severity: Severity) =>
    setSnackbar({ open: true, message, severity });

  // Switch handler — route by direction.
  const handleToggle = (dim: PersonalityDimension, nextChecked: boolean) => {
    if (nextChecked) {
      // FALSE → TRUE: hide (internal-only ON). One-click confirm.
      setPendingEnable(dim);
    } else {
      // TRUE → FALSE: reveal. Typed-name confirm.
      setPendingReveal(dim);
    }
  };

  const confirmEnable = () => {
    const dim = pendingEnable;
    if (!dim) return;
    setBusyKey(dim.dimension_key);
    dimensionsApi
      .updateDimension(dim.dimension_key, {
        is_internal_only: true,
        reason: `Marked "${dim.display_name}" internal-only from the Dimensions admin.`,
      })
      .then((returned) => {
        applyUpdatedRow(dim.dimension_key, returned, { is_internal_only: true });
        notify(`"${dim.display_name}" is now internal-only.`, 'success');
      })
      .catch((e) => notify(e instanceof Error ? e.message : 'Failed to update dimension.', 'error'))
      .finally(() => {
        setBusyKey(null);
        setPendingEnable(null);
      });
  };

  // ── τ + importance ────────────────────────────────────────────────────────
  const confirmTuning = (changes: Partial<DimensionTuningValues>, reason: string) => {
    const dim = pendingTuning;
    if (!dim) return;
    setBusyKey(dim.dimension_key);
    dimensionsApi
      .updateDimension(dim.dimension_key, { ...changes, reason })
      .then((returned) => {
        // The SERVER's row, not the draft: importance is NUMERIC(3,2) and what
        // was stored is the only thing this table may show.
        applyUpdatedRow(dim.dimension_key, returned, changes);
        notify(`Scoring updated for "${dim.display_name}".`, 'success');
        setPendingTuning(null);
      })
      .catch((e) => {
        // A 400 DIMENSION_BOUNDS names the bound it failed; show the backend's
        // own sentence and keep the dialog open so the value can be corrected.
        notify(e instanceof Error ? e.message : 'Failed to update dimension.', 'error');
        if (!(e instanceof QuillApiError && e.code === 'DIMENSION_BOUNDS')) {
          setPendingTuning(null);
        }
      })
      .finally(() => setBusyKey(null));
  };

  // ── charter-sensitive: the safe direction ─────────────────────────────────
  const confirmGate = () => {
    const dim = pendingGate;
    if (!dim) return;
    setBusyKey(dim.dimension_key);
    dimensionsApi
      .updateDimension(dim.dimension_key, {
        is_charter_sensitive: true,
        reason: `Marked "${dim.display_name}" charter-sensitive from the Dimensions admin.`,
      })
      .then((returned) => {
        applyUpdatedRow(dim.dimension_key, returned, { is_charter_sensitive: true });
        notify(`"${dim.display_name}" is now charter-sensitive.`, 'success');
      })
      .catch((e) => notify(e instanceof Error ? e.message : 'Failed to update dimension.', 'error'))
      .finally(() => {
        setBusyKey(null);
        setPendingGate(null);
      });
  };

  // ── charter-sensitive: the direction that takes a protection away ─────────
  const confirmUngate = (reason: string) => {
    const dim = pendingUngate;
    if (!dim) return;
    setBusyKey(dim.dimension_key);
    dimensionsApi
      .updateDimension(dim.dimension_key, {
        is_charter_sensitive: false,
        reason,
        confirm_charter_sensitive: dim.dimension_key,
      })
      .then((returned) => {
        applyUpdatedRow(dim.dimension_key, returned, { is_charter_sensitive: false });
        notify(`"${dim.display_name}" is no longer charter-sensitive.`, 'success');
        setPendingUngate(null);
      })
      .catch((e) => {
        if (
          e instanceof QuillApiError &&
          (e.code === 'CHARTER_SENSITIVE_CONFIRM_REQUIRED' || e.status === 428)
        ) {
          // The backend still wants the typed confirmation — keep the dialog
          // open rather than closing it over a refusal.
          notify('Clearing charter-sensitive requires typing the exact dimension key.', 'error');
        } else {
          notify(e instanceof Error ? e.message : 'Failed to update dimension.', 'error');
          setPendingUngate(null);
        }
      })
      .finally(() => setBusyKey(null));
  };

  const confirmReveal = (reason: string) => {
    const dim = pendingReveal;
    if (!dim) return;
    setBusyKey(dim.dimension_key);
    dimensionsApi
      .updateDimension(dim.dimension_key, {
        is_internal_only: false,
        reason,
        confirm_reveal: dim.dimension_key,
      })
      .then((returned) => {
        applyUpdatedRow(dim.dimension_key, returned, { is_internal_only: false });
        notify(`"${dim.display_name}" is now visible.`, 'success');
        setPendingReveal(null);
      })
      .catch((e) => {
        if (e instanceof QuillApiError && (e.code === 'DIMENSION_PERMANENTLY_INTERNAL' || e.status === 409)) {
          // F4: defensive — the table should not have offered a reveal control
          // for this row at all, but a stale page could. Surface the backend's
          // own sentence (it names the commitment) rather than a generic error.
          notify(e.message, 'error');
          setPendingReveal(null);
        } else if (e instanceof QuillApiError && (e.code === 'REVEAL_CONFIRM_REQUIRED' || e.status === 428)) {
          // The backend still wants the typed confirmation — keep the dialog
          // open and tell the admin to type the key.
          notify('Reveal requires typing the exact dimension key to confirm.', 'error');
        } else {
          notify(e instanceof Error ? e.message : 'Failed to reveal dimension.', 'error');
          setPendingReveal(null);
        }
      })
      .finally(() => setBusyKey(null));
  };

  const columns: Column<PersonalityDimension>[] = [
    {
      id: 'display_name',
      label: 'Dimension',
      render: (row) => (
        <Box>
          <Typography sx={{ fontWeight: 600 }}>{row.display_name}</Typography>
          <Typography
            variant="caption"
            sx={{ fontFamily: typography.fonts.mono, color: 'text.disabled', display: 'block' }}
          >
            {row.dimension_key}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'decay_half_life_days',
      label: 'Decay τ',
      align: 'right',
      render: (row) => (
        <Typography sx={{ fontFamily: typography.fonts.mono }}>
          {row.decay_half_life_days} d
        </Typography>
      ),
    },
    {
      id: 'importance',
      label: 'Importance',
      align: 'right',
      render: (row) => (
        <Typography sx={{ fontFamily: typography.fonts.mono }}>
          {Number(row.importance).toFixed(2)}
        </Typography>
      ),
    },
    {
      // One button for both numbers: they are one decision about one dimension,
      // and an inline cell input would turn a stray keystroke into a production
      // write.
      id: 'tuning',
      label: 'Scoring',
      align: 'center',
      render: (row) => (
        <Tooltip title="Edit decay half-life and importance">
          <span>
            <IconButton
              size="small"
              aria-label={`Edit scoring for ${row.dimension_key}`}
              disabled={busyKey === row.dimension_key}
              onClick={() => setPendingTuning(row)}
            >
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
    {
      id: 'is_charter_sensitive',
      label: 'Charter-sensitive',
      align: 'center',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          <Switch
            checked={row.is_charter_sensitive}
            disabled={busyKey === row.dimension_key}
            onChange={(_e, checked) =>
              (checked ? setPendingGate : setPendingUngate)(row)
            }
            slotProps={{ input: { 'aria-label': `Charter-sensitive for ${row.dimension_key}` } }}
          />
          {row.is_charter_sensitive && (
            <Chip
              label="Gated"
              size="small"
              sx={{
                backgroundColor: colors.content[50],
                color: colors.content.dark,
                fontWeight: 600,
                border: `1px solid ${colors.content.light}`,
              }}
            />
          )}
        </Box>
      ),
    },
    {
      id: 'is_internal_only',
      label: 'Internal-only',
      align: 'center',
      render: (row) =>
        // ── F4: a dimension held internal-only by commitment gets NO switch. ──
        // quill-engine unions this table with a hardcoded set, so revealing
        // coping_style could never actually reveal anything — the old switch
        // wrote the column, reported "now member-visible", and changed nothing
        // a member could see. A permanently-internal row now says so instead of
        // offering a control that cannot work.
        row.is_permanently_internal ? (
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            data-testid={`permanently-internal-${row.dimension_key}`}
          >
            <Tooltip title="Held internal-only by a standing product and legal commitment (migration 518 / decision 7.4). quill-engine enforces this in code, so it cannot be revealed from here — or from anywhere.">
              <Chip
                icon={<LockIcon sx={{ fontSize: 14 }} />}
                label="Always internal"
                size="small"
                sx={{
                  backgroundColor: colors.gray[100],
                  color: colors.gray[700],
                  fontWeight: 600,
                  border: `1px solid ${colors.gray[300]}`,
                }}
              />
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Switch
              checked={row.is_internal_only}
              disabled={busyKey === row.dimension_key}
              onChange={(_e, checked) => handleToggle(row, checked)}
              slotProps={{ input: { 'aria-label': `Internal-only for ${row.dimension_key}` } }}
            />
            {busyKey === row.dimension_key && <CircularProgress size={14} />}
          </Box>
        ),
    },
  ];

  const permanentlyInternal = (dimensions ?? []).filter((d) => d.is_permanently_internal);

  const header = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            backgroundColor: `${colors.content.main}1A`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            flexShrink: 0,
          }}
        >
          <LayersIcon sx={{ fontSize: 28, color: colors.content.main }} />
        </Box>
        <Box>
          <Typography
            sx={{
              fontFamily: typography.fonts.display,
              fontSize: '1.75rem',
              fontWeight: 700,
              color: colors.gray[900],
            }}
          >
            Dimensions
          </Typography>
          <Typography
            sx={{ fontFamily: typography.fonts.body, fontSize: typography.sizes.sm, color: 'text.secondary' }}
          >
            Personality dimensions the Quill engine tracks — decay, importance, and visibility.
          </Typography>
        </Box>
      </Box>
      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 3, color: status === 'up' ? 'success.main' : 'text.disabled' }}
      >
        ● {status === 'up' ? 'Connected' : status}
      </Typography>
    </>
  );

  const isEmpty = !loading && !loadError && dimensions !== null && dimensions.length === 0;

  return (
    <Box sx={{ p: 3 }}>
      {header}

      {loadError ? (
        <EmptyState
          icon="error"
          title="Couldn't load dimensions"
          description={loadError}
          action={{ label: 'Retry', onClick: load, variant: 'contained' }}
        />
      ) : isEmpty ? (
        <EmptyState
          icon="empty_box"
          title="No dimensions configured"
          description="The Quill engine has no personality dimensions registered yet."
        />
      ) : (
        <>
          {permanentlyInternal.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }} data-testid="permanently-internal-note">
              {permanentlyInternal.map((d) => d.display_name).join(', ')}
              {permanentlyInternal.length === 1 ? ' is' : ' are'} held internal-only by a standing
              commitment and cannot be revealed from here — quill-engine hides{' '}
              {permanentlyInternal.length === 1 ? 'it' : 'them'} in code regardless of this table.
            </Alert>
          )}
          <DataTable<PersonalityDimension>
            columns={columns}
            data={dimensions ?? []}
            loading={loading}
            emptyMessage="No dimensions configured"
          />
        </>
      )}

      {/* τ + importance — the two values quill-engine reads into its scoring. */}
      <DimensionTuningDialog
        open={pendingTuning != null}
        dimensionKey={pendingTuning?.dimension_key ?? ''}
        displayName={pendingTuning?.display_name ?? ''}
        current={{
          decay_half_life_days: pendingTuning?.decay_half_life_days ?? 0,
          importance: Number(pendingTuning?.importance ?? 0),
        }}
        loading={busyKey != null && pendingTuning != null}
        onConfirm={confirmTuning}
        onCancel={() => setPendingTuning(null)}
      />

      {/* Adding the charter-sensitive gate — one click. */}
      <QuillActionConfirm
        open={pendingGate != null}
        title="Make dimension charter-sensitive"
        message={
          pendingGate ? (
            <>
              Treat <strong>{pendingGate.display_name}</strong> as charter-sensitive in retrieval.
              Unconfirmed claims on this dimension will be held back from what a member&rsquo;s
              Quill says to their family. This applies to the live Quill engine (production).
            </>
          ) : (
            ''
          )
        }
        caption="Removing this gate later will require typing the dimension key to confirm."
        confirmLabel="Make charter-sensitive"
        pendingLabel="Saving…"
        loading={busyKey != null && pendingGate != null}
        onConfirm={confirmGate}
        onCancel={() => setPendingGate(null)}
      />

      {/* Removing the charter-sensitive gate — typed-key confirm. Same weight
          as the 7.4 reveal, because it is the same kind of act: a protection
          taken away from a bereaved family, from the next retrieval onwards. */}
      <RevealConfirmDialog
        open={pendingUngate != null}
        dimensionKey={pendingUngate?.dimension_key ?? ''}
        displayName={pendingUngate?.display_name ?? ''}
        title="Remove charter-sensitive gate"
        body={
          <>
            You are about to stop treating <strong>{pendingUngate?.display_name}</strong> as
            charter-sensitive. quill-engine tags facts on this dimension sensitive from this
            column, and the Mirror drops the sensitive-and-unconfirmed ones before they can be
            said. Without the gate, unconfirmed claims on this dimension become eligible to be
            spoken to a bereaved family. This applies to the live Quill engine (production). To
            confirm, type the dimension key exactly.
          </>
        }
        confirmLabel="Remove the gate"
        pendingLabel="Removing…"
        reasonLabel="Reason for removing the gate"
        loading={busyKey != null && pendingUngate != null}
        onConfirm={confirmUngate}
        onCancel={() => setPendingUngate(null)}
      />

      {/* Turning internal-only ON — one click. */}
      <QuillActionConfirm
        open={pendingEnable != null}
        title="Make dimension internal-only"
        message={
          pendingEnable ? (
            <>
              Hide <strong>{pendingEnable.display_name}</strong> from member-visible surfaces by
              marking it internal-only. This applies to the live Quill engine (production).
            </>
          ) : (
            ''
          )
        }
        caption="Revealing it again later will require typing the dimension key to confirm."
        confirmLabel="Make internal-only"
        pendingLabel="Saving…"
        loading={busyKey != null && pendingEnable != null}
        onConfirm={confirmEnable}
        onCancel={() => setPendingEnable(null)}
      />

      {/* Turning internal-only OFF — typed-name reveal guard (7.4). */}
      <RevealConfirmDialog
        open={pendingReveal != null}
        dimensionKey={pendingReveal?.dimension_key ?? ''}
        displayName={pendingReveal?.display_name ?? ''}
        loading={busyKey != null && pendingReveal != null}
        onConfirm={confirmReveal}
        onCancel={() => setPendingReveal(null)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DimensionsPage;
